import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth } from '@/lib/api'

import type {
  CatalogueRow,
  FactBinding,
  FactCatalogueResponse,
  FactSource,
  NormalizedCatalogue,
} from './types'

const REGISTERED_TYPE_ALIASES: Record<string, string> = {
  str: 'string',
  bool: 'boolean',
  int: 'integer',
  float: 'number',
  json: 'object',
  date: 'date',
}

function normalizeValueType(valueType: string | null | undefined): string {
  if (!valueType) return ''
  return REGISTERED_TYPE_ALIASES[valueType] ?? valueType
}

function factSource(fact: FactBinding): FactSource {
  if (fact.definition?.kind === 'constant') return 'entered'
  if (fact.definition?.kind === 'expr' || fact.definition?.kind === 'block') {
    return 'derived'
  }
  return 'data'
}

function factRow(fact: FactBinding): CatalogueRow {
  if (fact.scope === 'duty' && (!fact.duty_id || !fact.duty_name?.trim())) {
    throw new Error('Duty-scoped fact is missing its duty ID or name')
  }
  const type = normalizeValueType(fact.value_type)
  return {
    name: fact.binding,
    scope: fact.scope,
    dutyId: fact.duty_id,
    dutyName: fact.duty_name,
    definition: fact.definition,
    type,
    types: type ? [type] : [],
    source: factSource(fact),
    description: fact.description ?? '',
    ...(fact.coverage
      ? {
          coverage: {
            availableCount: fact.coverage.available_count,
            subjectCount: fact.coverage.subject_count,
            basis: fact.coverage.basis,
          },
        }
      : {}),
  }
}

/**
 * Map the author-facing bindings into table rows.
 */
export function normalizeCatalogue(
  response: Partial<FactCatalogueResponse> | null | undefined
): NormalizedCatalogue {
  const facts = Array.isArray(response?.facts)
    ? response.facts.filter(Boolean)
    : []
  return { rows: facts.map(factRow) }
}

export async function fetchFactCatalogue(
  token: string
): Promise<NormalizedCatalogue> {
  const response = await fetchJsonWithAuth<FactCatalogueResponse>({
    token,
    url: `${AGENT_URL}/facts/catalogue`,
    errorMessage: 'Failed to load fact catalogue',
  })
  return normalizeCatalogue(response)
}
